/* ============================================================
   Pilatix App — Download bar JS
   Detects iOS / Android / desktop via UA + position-driven show/hide.

   Behaviour:
   - Desktop: bar removed from DOM entirely (zero cost)
   - Mobile + below show-threshold: hidden (slid down + faded)
   - Mobile + past show-threshold: visible (slid up + opaque)
   - Direction-AGNOSTIC: scrolling up while past threshold does NOT hide.
     Bar only hides when user scrolls back PAST the threshold toward the top.
   - Threshold = bottom of §1 hero (.pltx2-fplayer) - 25% viewport;
     fallback = 80% of viewport height if §1 not present.
   - Cached threshold; recomputed on resize.
   ============================================================ */
(function () {
  'use strict';

  var FALLBACK_RATIO = 0.8;      // share of viewport before show (no hero case)
  var HERO_TAIL_RATIO = 0.25;    // overlap into top of next section

  function detectPlatform() {
    var ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/i.test(ua) && !window.MSStream) return 'ios';
    // iPadOS 13+ reports as Macintosh; sniff touch capability
    if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    // Fallback: narrow viewport (= mobile-sized window or desktop resized
    // for testing). Default unknown-mobile to 'android' button since
    // Android has higher market share globally; user can manually pick
    // either store from the link if they really want the other one.
    if (window.innerWidth < 735) return 'android';
    return 'desktop';
  }

  function setup(root) {
    if (!root || root.__pltx2FdlSetup) return;
    root.__pltx2FdlSetup = true;

    // Remove the HTML hidden attribute unconditionally — visibility from here
    // on is controlled by CSS (opacity + transform) and the @media query.
    // (Leaving the attribute would make CSS display:none win regardless.)
    root.hidden = false;

    var platform = detectPlatform();

    // Desktop: keep the element in DOM but rely on CSS @media to hide it.
    // (Previously we .remove()'d it — but that made testing on a resized
    // desktop browser impossible, since UA stays 'desktop' on window resize.
    // Letting CSS handle it means resizing the window to <735px now reveals
    // the bar for testing, which mirrors actual mobile behavior.)
    if (platform === 'desktop') {
      root.setAttribute('data-platform', 'desktop');
      return;
    }

    root.setAttribute('data-platform', platform);

    // ---- Threshold computation (cached, invalidated on resize) ----
    var cachedThreshold = null;
    function getThreshold() {
      if (cachedThreshold !== null) return cachedThreshold;
      var hero = document.querySelector('.pltx2-fplayer');
      if (hero) {
        var rect = hero.getBoundingClientRect();
        // Absolute page Y where the threshold lives:
        //   current scrollY + hero bottom - 25% viewport
        // (= "user has scrolled almost past the hero")
        cachedThreshold = Math.max(
          0,
          window.scrollY + rect.bottom - window.innerHeight * HERO_TAIL_RATIO
        );
      } else {
        cachedThreshold = window.innerHeight * FALLBACK_RATIO;
      }
      return cachedThreshold;
    }
    function invalidateThreshold() { cachedThreshold = null; }

    // ---- State ----
    var isVisible = false;
    var ticking = false;
    var isDrawerOpen = false;     // hamburger menu drawer currently open?

    // Cached absolute Y of §7 buy section's top edge. Invalidated on
    // resize and shopify:section:load (which can shift layout).
    var cachedBuyTopY = null;
    function getBuyTopY() {
      if (cachedBuyTopY !== null) return cachedBuyTopY;
      var buy = document.querySelector('.pltx2-fbuy');
      if (!buy) {
        cachedBuyTopY = Infinity;  // No buy section on this page → never hide due to buy
        return cachedBuyTopY;
      }
      cachedBuyTopY = window.scrollY + buy.getBoundingClientRect().top;
      return cachedBuyTopY;
    }
    function invalidateBuyTopY() { cachedBuyTopY = null; }

    function show() {
      if (isVisible) return;
      isVisible = true;
      root.classList.add('is-visible');
    }
    function hide() {
      if (!isVisible) return;
      isVisible = false;
      root.classList.remove('is-visible');
    }

    function update() {
      ticking = false;
      var y = window.scrollY;
      var threshold = getThreshold();

      // Hamburger drawer is open — slide down out of the way so it doesn't
      // overlap the menu items. Triggers the existing CSS slide-down via
      // .is-visible removal. The bar comes back on drawer close.
      if (isDrawerOpen) {
        hide();
        return;
      }

      // User has reached OR scrolled past §7 buy section — hide forever
      // (until they scroll back up above §7). Trigger fires when §7 is
      // ~15% into the viewport from the bottom. Once past, the bar stays
      // hidden through the FAQ / footer too, no awkward reappearance.
      var buyTopY = getBuyTopY();
      if (y + window.innerHeight * 0.15 >= buyTopY) {
        hide();
        return;
      }

      // Position-only otherwise: past threshold = show, below threshold = hide.
      // Scroll direction is NOT considered — once the user has earned the
      // bar by scrolling past the hero, scrolling back up doesn't hide it
      // until they cross back below the threshold itself.
      if (y >= threshold) show();
      else                hide();
    }

    // ---- Watch the hamburger drawer open/close ----
    // Theme renders a <details id="Details-menu-drawer-container"> for the
    // mobile nav. The presence/absence of the `open` attribute = drawer state.
    // MutationObserver lets us react instantly without polling.
    function observeDrawer() {
      var drawer = document.getElementById('Details-menu-drawer-container');
      if (!drawer || !('MutationObserver' in window)) return;
      isDrawerOpen = drawer.hasAttribute('open');

      var obs = new MutationObserver(function () {
        var wasOpen = isDrawerOpen;
        isDrawerOpen = drawer.hasAttribute('open');
        if (wasOpen === isDrawerOpen) return;
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      });
      obs.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }

    // §7 visibility is now derived from absolute Y position (see getBuyTopY
    // and the update() handler). No IntersectionObserver needed — the same
    // calculation that detects "entering §7" also handles "scrolled past §7"
    // correctly, so the bar stays hidden through FAQ/footer instead of
    // re-appearing awkwardly.

    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }

    function onResize() {
      invalidateThreshold();
      invalidateBuyTopY();
      onScroll();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    // Recompute when sections live-render in the theme editor — any layout
    // change could shift §7's offset or the hero's height
    document.addEventListener('shopify:section:load',   function () {
      invalidateThreshold(); invalidateBuyTopY();
    });
    document.addEventListener('shopify:section:unload', function () {
      invalidateThreshold(); invalidateBuyTopY();
    });

    // Hide the bar when the hamburger drawer opens (the "shy" slide-down)
    observeDrawer();

    // Initial state on load (e.g. user lands on mid-page anchor)
    update();
  }

  function init() {
    var roots = document.querySelectorAll('[data-pltx2-fdl-root]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector
      ? e.target.querySelector('[data-pltx2-fdl-root]')
      : null;
    if (root) setup(root);
  });
})();
